import * as Yup from 'yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFAutocomplete,
  RHFTextField,
} from 'src/components/hook-form';
import { useUpdateAccountMutation } from 'src/context/api/auth';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { countries } from 'src/assets/data';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useAuth } from 'src/auth';

// ----------------------------------------------------------------------

export default function AccountGeneralSettings() {
  const { t } = useLocales();
  const [update, response] = useUpdateAccountMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const UpdateUserSchema = Yup.object().shape({
    profile: Yup.object().shape({
      id: Yup.number().nullable(),
      firstname: Yup.string(),
      lastname: Yup.string(),
      phone: Yup.string().required(t('dashboard.account.phone_number_is_required')),
      job: Yup.string().nullable(),
    }),
    address: Yup.object().shape(
      {
        id: Yup.number().nullable(),
        city: Yup.string().optional(),
        country: Yup.string().optional(),
      }
    ),
    email: Yup.string().required(t('dashboard.account.email_is_required')).email(t('dashboard.account.email_must_be_a_valid_email_address')),
  });

  const defaultValues = {
    profile: {
      id: user?.profile?.id || null,
      firstname: user?.profile?.firstname || '',
      lastname: user?.profile?.lastname || '',
      phone: user?.profile?.phone || '',
      job: user?.profile?.job || '',
    },
    address: {
      id: user?.addresses?.[0]?.id || null,
      city: user?.addresses?.[0]?.city || '',
      country: user?.addresses?.[0]?.country || '',
    },
    email: user?.email || '',
  };

  const methods = useForm({
    resolver: yupResolver(UpdateUserSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    setValue('profile.id', user?.profile?.id || undefined);
    setValue('profile.firstname', user?.profile?.firstname || '');
    setValue('profile.lastname', user?.profile?.lastname || '');
    setValue('address.id', user?.addresses?.[0]?.id || undefined);
    setValue('address.country', user?.addresses?.[0]?.country || '');
    setValue('address.city', user?.addresses?.[0]?.city || '');
    setValue('profile.phone', user?.profile?.phone || '');
    setValue('profile.job', user?.profile?.job || '');
    setValue('email', user?.email || '');
  }, [user]);

  useEffect(() => {
    try {
      if (!response.isError && response.status === QueryStatus.fulfilled) {
        enqueueSnackbar(t('dashboard.account.update_success'));
      } else if (response.isError) {
        enqueueSnackbar<'error'>(t('dashboard.account.cannot_update'), { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  }, [response]);

  return (

    <FormProvider autoComplete="off" methods={methods} onSubmit={handleSubmit(update)}>
      <input type="hidden" {...methods.register('profile.id')} />
      <Box
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
      >
        <RHFTextField name="profile.firstname" label={t('dashboard.account.first_name')} />
        <RHFTextField name="profile.lastname" label={t('dashboard.account.last_name')} />
        <RHFTextField disabled={!user?.is_updatable} name="email" label={t('dashboard.account.email_address')} />
        <RHFAutocomplete
          disabled={!user?.is_updatable}
          name="address.country"
          label={t('dashboard.global.country')}
          options={countries.map((country) => country.label)}
          getOptionLabel={(option) => option}
          renderOption={(props, option) => {
            const { code, label, phone } = countries.filter(
              (country) => country.label === option
            )[0] || {};

            if (!label) {
              return null;
            }

            return (
              <li {...props} key={label}>
                <Iconify
                  key={label}
                  icon={`circle-flags:${code.toLowerCase()}`}
                  width={28}
                  sx={{ mr: 1 }}
                />
                {label} ({code}) +{phone}
              </li>
            );
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>

        <LoadingButton sx={{ mt: 3 }} type="submit" variant="contained" loading={isSubmitting}>
         {t('dashboard.global.save_changes')}
        </LoadingButton>
      </Stack>
    </FormProvider>
  )
}
